/**
 * Per-AP Exporter cockpit settings (CONCEPT_EXPORTER_EMAIL_TRACKING.md §8).
 *
 * Phase A: persisted in localStorage. Backend implementation deferred.
 * Each setting is overridable per dispatch in the composer.
 */

import { create } from "zustand"

export interface ExporterSettings {
  /** Default reminder days when starting a new email dispatch. */
  defaultFollowUpDays: number
  /** AP-owned reply-to address; null if none. */
  replyToEmail: string | null
  /** Default subject (placeholders allowed: {{ap.title}}, {{record.name}}). */
  defaultSubject: string
  /** Default body (placeholders allowed). */
  defaultBody: string
}

const STORAGE_KEY = "qms.exporter.settings"

// FALLBACK values are safety nets only — VersandComposer calls translate("QMS/EXPORTER/DEFAULT_SUBJECT")
// and translate("QMS/EXPORTER/DEFAULT_BODY") first, so these German strings are only reached if the
// translation system itself is broken. Do not localise these; they are last-resort backstops.
const FALLBACK: ExporterSettings = {
  defaultFollowUpDays: 7,
  replyToEmail: null,
  defaultSubject: "Anfrage {{ap.title}}",
  defaultBody:
    "Sehr geehrte Damen und Herren,\n\n" +
    "bitte füllen Sie den anhängenden Fragebogen aus und senden ihn " +
    "innerhalb von {{antwort_frist}} Tagen zurück.\n\n" +
    "Mit freundlichen Grüßen",
}

function loadAll(): Record<string, ExporterSettings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === "object")
      return parsed as Record<string, ExporterSettings>
    return {}
  } catch {
    return {}
  }
}

function saveAll(map: Record<string, ExporterSettings>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

interface SettingsState {
  byAp: Record<string, ExporterSettings>
  get: (apId: string) => ExporterSettings
  patch: (apId: string, patch: Partial<ExporterSettings>) => void
  reset: (apId: string) => void
}

export const useExporterSettingsStore = create<SettingsState>(
  (set, getStore) => ({
    byAp: loadAll(),

    get: (apId) => {
      const stored = getStore().byAp[apId]
      return stored ? { ...FALLBACK, ...stored } : { ...FALLBACK }
    },

    patch: (apId, patch) =>
      set((state) => {
        const current = state.byAp[apId] ?? { ...FALLBACK }
        const next = { ...current, ...patch }
        const map = { ...state.byAp, [apId]: next }
        saveAll(map)
        return { byAp: map }
      }),

    reset: (apId) =>
      set((state) => {
        const map = { ...state.byAp }
        delete map[apId]
        saveAll(map)
        return { byAp: map }
      }),
  }),
)

export function applyDefaultsFromConfig(
  apId: string,
  cfg: {
    apOwnEmail?: string
    bodyTemplateDefault?: string
    subjectTemplateDefault?: string
    defaultFollowUpDays?: number
  },
): void {
  const store = useExporterSettingsStore.getState()
  const stored = store.byAp[apId]
  if (stored) return // already user-customised; leave as-is
  store.patch(apId, {
    defaultFollowUpDays:
      cfg.defaultFollowUpDays ?? FALLBACK.defaultFollowUpDays,
    replyToEmail: cfg.apOwnEmail ?? FALLBACK.replyToEmail,
    defaultSubject: cfg.subjectTemplateDefault ?? FALLBACK.defaultSubject,
    defaultBody: cfg.bodyTemplateDefault ?? FALLBACK.defaultBody,
  })
}
